import type { ComponentProps } from 'react';
import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import type { Nullable } from 'corgi-types';
import type {
  BackgroundColorProps,
  BorderRadiusProps,
  FlexProps,
  MaxWidthProps,
  PositionProps,
  WidthProps,
} from 'styled-system';
import { backgroundColor, borderRadius, flex, maxWidth, position, width } from 'styled-system';

import Alert from 'common/components/Alert';
import { Flex } from 'common/components/Flex';
import { HeadlineH6 } from 'common/components/Typography';
import { MessageType } from 'common/enums/Alert';
import { useGQLLocale } from 'common/hooks/locale';
import { useLocalization } from 'common/providers/LocaleProvider';
import type { BoxSizingProps } from 'common/theme/system-utilities';
import { boxSizing } from 'common/theme/system-utilities';
import BirthdayForm from './BirthdayForm';
import CloseButton from './CloseButton';
import giftSvg from './icons/gift.svg';
import { getIsHomeClubNewsletterSubscribed } from './utils/query-subscriptionts';

interface BirthdayCardProps {
  setSuccessMessage: (text: Nullable<string>) => void;
  onBirthdayCardClose: () => void;
  className?: string;
}

const Headline = styled(HeadlineH6)<WidthProps>`
  ${width};
`;

const Container = styled(Flex)<
  BackgroundColorProps & BorderRadiusProps & PositionProps & BoxSizingProps
>`
  ${backgroundColor};
  ${borderRadius};
  ${boxSizing};
  ${position};
  ${width};
`;

const containerStyles: ComponentProps<typeof Container> = {
  backgroundColor: 'sys.primary.background.weak',
  flexDirection: ['column', null, 'row'],
  pr: ['sp_16', null, '56px'],
  boxSizing: 'border-box',
  borderRadius: 'medium',
  alignItems: 'center',
  position: 'relative',
  p: 'sp_16',
  width: 1,
};

const StyledBirthdayForm = styled(BirthdayForm)<FlexProps & MaxWidthProps & WidthProps>`
  ${maxWidth};
  ${width};
  ${flex};
`;

const BirthdayCard = ({ className, setSuccessMessage, onBirthdayCardClose }: BirthdayCardProps) => {
  const { t } = useLocalization();
  const gqlLocale = useGQLLocale();
  const [errorMessage, setErrorMessage] = useState(null);
  const [isHomeClubNewsletterSubscribed, setIsHomeClubNewsletterSubscribed] = useState(true);

  useEffect(() => {
    getIsHomeClubNewsletterSubscribed(gqlLocale).then(isSubscribed => {
      setIsHomeClubNewsletterSubscribed(isSubscribed);
    });
  }, [gqlLocale]);

  return (
    <>
      {!!errorMessage && (
        <Alert type={MessageType.Error} mt="sp_12" mb="sp_0">
          {errorMessage}
        </Alert>
      )}
      <Container className={className} {...containerStyles}>
        <img src={giftSvg} alt="" />
        <Headline
          textAlign={['center', null, 'left']}
          mb={['sp_24', null, 'sp_0']}
          ml={['sp_0', null, 'sp_8']}
          mr={['sp_0', null, 'sp_24', 'sp_32']}
          width={['100%', null, '30%']}
        >
          {t('h24_hc_birthday_card_title')}
        </Headline>
        <StyledBirthdayForm
          flex={1}
          maxWidth="536px"
          width={['100%', null, 'auto']}
          setErrorMessage={setErrorMessage}
          setSuccessMessage={setSuccessMessage}
          withHomeClubCheckbox={!isHomeClubNewsletterSubscribed}
        />
        <CloseButton onClick={onBirthdayCardClose} />
      </Container>
    </>
  );
};

export default BirthdayCard;
